import { useEffect, useRef } from 'react';
import {
  useKeyboardShortcuts,
  matchesEvent,
  parseKeys,
  type ShortcutCategory,
} from '@/contexts/KeyboardShortcutContext';
import { config as productConfig } from '@/src/config';

interface GlobalShortcutsProps {
  onNavigate: (tab: string) => void;
  onOpenAISearch: () => void;
  /** Open the Copilot — the in-app agent. */
  onOpenCopilot?: () => void;
  onComposeEmail?: () => void;
  onOpenCommandPalette?: () => void;
  onOpenGoToPage?: () => void;
  onOpenOrgSwitcher?: () => void;
}

interface ShortcutDef {
  id: string;
  keys: string;
  label: string;
  description: string;
  category: ShortcutCategory;
}

/**
 * All shortcut definitions — used for both execution and help dialog display.
 * Inbox action shortcuts (j/k/e/r/f/o/u) are registered for display only;
 * their execution is handled by EmailInboxView's own listener.
 */
const SHORTCUT_DEFS: ShortcutDef[] = [
  // ── General ──
  { id: 'general.help', keys: '?', label: 'Show keyboard shortcuts', description: 'Toggle this help dialog', category: 'general' },
  { id: 'general.home', keys: 'alt+h', label: 'Go to Home', description: 'Navigate to home dashboard', category: 'navigation' },

  // ── Search ──
  { id: 'search.command', keys: 'mod+k', label: 'Command Palette', description: 'Open command palette for navigation, search, and actions', category: 'search' },
  { id: 'search.goto', keys: 'alt+shift+p', label: 'Go to Page', description: 'Jump directly to any page', category: 'search' },
  { id: 'search.ai', keys: 'mod+shift+k', label: 'AI Search', description: 'Open global AI search directly', category: 'search' },
  { id: 'search.copilot', keys: 'mod+j', label: 'Ask Copilot', description: 'Open the in-app assistant', category: 'search' },
  { id: 'search.focus', keys: '/', label: 'Focus search', description: 'Focus the search bar on current page', category: 'search' },

  // ── Organization ──
  { id: 'org.switch', keys: 'alt+p', label: 'Switch Organization', description: 'Open organization switcher', category: 'navigation' },

  // ── Navigation ──
  { id: 'nav.inbox', keys: 'alt+i', label: 'Go to Inbox', description: 'Open communication inbox', category: 'navigation' },
  { id: 'nav.calendar', keys: 'alt+c', label: 'Go to Calendar', description: 'Open calendar', category: 'navigation' },
  { id: 'nav.members', keys: 'alt+m', label: 'Go to Members', description: 'Open member list', category: 'navigation' },
  { id: 'nav.analytics', keys: 'alt+a', label: 'Go to Analytics', description: 'Open analytics dashboard', category: 'navigation' },

  // ── Marketing ──
  { id: 'nav.marketing', keys: 'alt+k', label: 'Go to Marketing', description: 'Open marketing automation', category: 'marketing' },
  { id: 'nav.campaigns', keys: 'alt+g', label: 'Go to Campaigns', description: 'Open campaign management', category: 'marketing' },
  { id: 'nav.bulk-comms', keys: 'alt+b', label: 'Go to Bulk Communications', description: 'Open bulk messaging', category: 'marketing' },
  { id: 'nav.web-presence', keys: 'alt+w', label: 'Go to Design Studio', description: 'Open AI-powered design studio', category: 'marketing' },

  // ── Sales ──
  { id: 'nav.sales', keys: 'alt+s', label: 'Go to Sales Pipeline', description: 'Open sales pipeline', category: 'sales' },
  { id: 'nav.contacts', keys: 'alt+o', label: 'Go to Contacts', description: 'Open contact directory', category: 'sales' },
  { id: 'nav.leads', keys: 'alt+l', label: 'Go to Lead Builder', description: 'Open lead builder', category: 'sales' },
  { id: 'nav.cold-outreach', keys: 'alt+r', label: 'Go to Cold Outreach', description: 'Open cold outreach campaigns', category: 'sales' },
  { id: 'nav.revops', keys: 'alt+v', label: 'Go to RevOps', description: 'Open revenue operations', category: 'sales' },

  // ── Email ──
  { id: 'inbox.compose', keys: 'alt+n', label: 'Compose new message', description: 'Start a new email or message', category: 'email' },

  // ── Activities ──
  { id: 'activities.new-task', keys: 'alt+t', label: 'New task', description: 'Create a new task', category: 'activities' },
  { id: 'activities.new-note', keys: 'alt+j', label: 'New note', description: 'Create a new note or case note', category: 'activities' },

  // ── Communications ──
  { id: 'comms.send', keys: 'mod+enter', label: 'Send message', description: 'Send the current message', category: 'communications' },

  // ── Triveny Quick Actions ──
  // Pattern: alt+shift+<letter> for creation/run actions to keep them
  // distinct from alt+<letter> navigation. Each shortcut navigates to
  // the destination page AND dispatches a `triveny-action` custom event
  // the page listens for to open its create-modal. Mirrors the
  // CommandPalette's handleSelect dispatches one-to-one.
  { id: 'triveny.run_campaign', keys: 'alt+shift+c', label: 'Run a campaign', description: 'Open Marketing and start a new campaign', category: 'triveny' },
  { id: 'triveny.new_deal', keys: 'alt+shift+d', label: 'Create a deal', description: 'Open Pipeline and add a new opportunity', category: 'triveny' },
  { id: 'triveny.new_contact', keys: 'alt+shift+u', label: 'New contact', description: 'Open Contacts and create a new contact', category: 'triveny' },
  { id: 'triveny.new_ticket', keys: 'alt+shift+s', label: 'Open a support ticket', description: 'Open Customer Support and create a ticket', category: 'triveny' },
  { id: 'triveny.new_page', keys: 'alt+shift+l', label: 'New landing page', description: 'Open Studio and start a new landing page', category: 'triveny' },

  // ── Email actions (display-only — handled by EmailInboxView directly) ──
  { id: 'email.next', keys: 'j', label: 'Next email', description: 'j or ↓ arrow key', category: 'email' },
  { id: 'email.prev', keys: 'k', label: 'Previous email', description: 'k or ↑ arrow key', category: 'email' },
  { id: 'email.open', keys: 'o', label: 'Open/expand email', description: 'Inbox & Sales email views', category: 'email' },
  { id: 'email.reply', keys: 'r', label: 'Reply', description: 'Inbox & Sales email views', category: 'email' },
  { id: 'email.forward', keys: 'f', label: 'Forward', description: 'Sales email view', category: 'email' },
  { id: 'email.archive', keys: 'e', label: 'Archive', description: 'Inbox & Sales email views', category: 'email' },
  { id: 'email.select', keys: 'x', label: 'Toggle selection', description: 'Sales email view', category: 'email' },
  { id: 'email.compose', keys: 'c', label: 'Compose new', description: 'Sales email view', category: 'email' },
  { id: 'email.back', keys: 'u', label: 'Close expanded', description: 'Inbox & Sales email views', category: 'email' },
  { id: 'email.delete', keys: 'shift+d', label: 'Delete', description: 'Inbox & Sales email views', category: 'email' },
  { id: 'email.toggle-read', keys: 'shift+u', label: 'Toggle read', description: 'Communications inbox', category: 'email' },
];

/** IDs of email action shortcuts handled by EmailInboxView directly */
const EMAIL_ACTION_IDS = new Set([
  'email.next', 'email.prev', 'email.open', 'email.reply', 'email.forward',
  'email.archive', 'email.select', 'email.compose', 'email.back', 'email.delete', 'email.toggle-read',
]);

export function GlobalShortcuts({ onNavigate, onOpenAISearch, onOpenCopilot, onComposeEmail, onOpenCommandPalette, onOpenGoToPage, onOpenOrgSwitcher }: GlobalShortcutsProps) {
  const { setIsHelpOpen, getKeysForShortcut, isHelpOpen, registerShortcut } = useKeyboardShortcuts();

  // Refs so the keydown handler always reads current values
  const propsRef = useRef({ onNavigate, onOpenAISearch, onOpenCopilot, onComposeEmail, onOpenCommandPalette, onOpenGoToPage, onOpenOrgSwitcher });
  propsRef.current = { onNavigate, onOpenAISearch, onOpenCopilot, onComposeEmail, onOpenCommandPalette, onOpenGoToPage, onOpenOrgSwitcher };
  const setIsHelpOpenRef = useRef(setIsHelpOpen);
  setIsHelpOpenRef.current = setIsHelpOpen;
  const isHelpOpenRef = useRef(isHelpOpen);
  isHelpOpenRef.current = isHelpOpen;
  const getKeysRef = useRef(getKeysForShortcut);
  getKeysRef.current = getKeysForShortcut;

  // Register all shortcuts for help dialog display in a single useEffect (no hooks-in-loop)
  useEffect(() => {
    for (const def of SHORTCUT_DEFS) {
      registerShortcut({
        id: def.id,
        keys: def.keys,
        label: def.label,
        description: def.description,
        category: def.category,
        action: () => {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerShortcut]);

  // Execute shortcut action by ID
  const executeAction = (id: string) => {
    const { onNavigate: nav, onOpenAISearch: search, onOpenCopilot: copilot, onComposeEmail: compose, onOpenCommandPalette: cmdPalette, onOpenGoToPage: goToPage, onOpenOrgSwitcher: orgSwitcher } = propsRef.current;
    switch (id) {
      case 'general.help':
        setIsHelpOpenRef.current(prev => !prev);
        break;
      case 'general.home':
        // Triveny home is the full-bleed launcher (triveny-home), which
        // renders via an early-return in App.tsx that hides the sidebar.
        // The legacy 'home' tab renders inside the standard layout —
        // navigating there with the sidebar visible cuts the home page
        // view because TrivenyHome is full-bleed-only.
        nav(productConfig.isTriveny ? 'triveny-home' : 'home');
        break;
      case 'search.command': if (cmdPalette) cmdPalette(); break;
      case 'search.goto': if (goToPage) goToPage(); break;
      case 'search.ai': search(); break;
      case 'search.copilot': if (copilot) copilot(); break;
      case 'org.switch': if (orgSwitcher) orgSwitcher(); break;
      case 'search.focus': {
        const input = document.querySelector<HTMLInputElement>(
          'input[type="text"][placeholder*="Search"], input[type="search"]'
        );
        if (input) { input.focus(); input.select(); }
        break;
      }
      case 'nav.inbox':
        // The Inbox is the Messaging workspace for everyone: the healthcare
        // 2-Way Inbox ('inbox' tab) is retired (TRUS-709).
        nav('workspace-messaging');
        break;
      case 'nav.calendar': nav('calendar'); break;
      case 'nav.members': nav('members'); break;
      case 'nav.analytics': nav('analytics'); break;
      case 'nav.marketing': nav('platform-marketing'); break;
      case 'nav.campaigns': nav('campaigns'); break;
      case 'nav.bulk-comms': nav('bulk-communications'); break;
      case 'nav.web-presence': nav('platform-web-presence'); break;
      case 'nav.sales': nav('platform-sales'); break;
      case 'nav.contacts': nav('platform-contacts'); break;
      case 'nav.leads': nav('platform-lead-builder'); break;
      case 'nav.cold-outreach': nav('platform-cold-outreach'); break;
      case 'nav.revops': nav('platform-revops'); break;
      case 'inbox.compose':
        if (compose) { compose(); } else { nav('workspace-messaging'); }
        break;
      case 'activities.new-task':
        document.dispatchEvent(new CustomEvent('keyboard-shortcut', { detail: { action: 'new-task' } }));
        break;
      case 'activities.new-note':
        document.dispatchEvent(new CustomEvent('keyboard-shortcut', { detail: { action: 'new-note' } }));
        break;
      case 'comms.send':
        document.dispatchEvent(new CustomEvent('keyboard-shortcut', { detail: { action: 'send' } }));
        break;

      // ── Triveny Quick Actions ──
      // Each pair nav + dispatch the same `triveny-action` event the
      // command palette uses, so destination pages can listen once and
      // handle both entry points uniformly. 100ms delay matches the
      // palette's pattern (gives the destination component time to
      // mount before the event fires).
      case 'triveny.run_campaign':
        nav('platform-marketing');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-campaign' })), 100);
        break;
      case 'triveny.new_deal':
        nav('platform-sales');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-deal' })), 100);
        break;
      case 'triveny.new_contact':
        nav('platform-contacts');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-contact' })), 100);
        break;
      case 'triveny.new_ticket':
        nav('platform-support');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-ticket' })), 100);
        break;
      case 'triveny.new_page':
        nav('platform-web-presence');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-page' })), 100);
        break;
    }
  };

  // ── Direct keydown handler — no registry dependency ──
  useEffect(() => {
    console.log('[Keyboard Shortcuts] Handler attached — press ? for help, Alt+key for navigation');

    const handler = (e: KeyboardEvent) => {
      // When help dialog is open, only allow ? toggle and Esc
      if (isHelpOpenRef.current) {
        if (e.key === '?') {
          e.preventDefault();
          setIsHelpOpenRef.current(prev => !prev);
        }
        return;
      }

      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
                      target.tagName === 'SELECT' || target.isContentEditable;
      // An element can claim chords for itself (e.g. the message composer takes
      // mod+k for "Insert link"): <input data-claims-shortcuts="mod+k">.
      const claimed = (target.closest?.('[data-claims-shortcuts]') as HTMLElement | null)
        ?.getAttribute('data-claims-shortcuts')?.split(/\s+/) ?? [];

      for (const def of SHORTCUT_DEFS) {
        // Skip inbox action shortcuts — handled by EmailInboxView's own listener
        if (EMAIL_ACTION_IDS.has(def.id)) continue;

        // Get customized keys (or default)
        const keys = getKeysRef.current(def.id, def.keys);

        if (claimed.includes(keys)) continue;

        const parsed = parseKeys(keys);
        const isSingleKey = !parsed.mod && !parsed.shift && !parsed.alt;
        if (isSingleKey && isInput) continue;

        if (matchesEvent(e, keys)) {
          e.preventDefault();
          e.stopPropagation();
          console.log('[Keyboard Shortcuts] Matched:', def.id, '→', keys);
          executeAction(def.id);
          return;
        }
      }
    };

    window.addEventListener('keydown', handler, true);

    // Listen for toggle-help event from command palette
    const helpHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.action === 'toggle-help') {
        setIsHelpOpenRef.current(prev => !prev);
      }
    };
    window.addEventListener('keyboard-shortcut', helpHandler);

    return () => {
      window.removeEventListener('keydown', handler, true);
      window.removeEventListener('keyboard-shortcut', helpHandler);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default GlobalShortcuts;
